import { createContext } from '@lib/mobx/context';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import { Renderer } from '@stores/Renderer/Renderer';
import { action, computed, makeObservable, observable } from 'mobx';

export class ExtendedMediaId {
  constructor(
    public readonly mediaId: string,
    public readonly libraryId: string,
  ) {
    makeObservable(this);
    this.mediaId = mediaId;
    this.libraryId = libraryId;
  }
}

export class BeginEndTimestamp {
  constructor(
    public readonly startTime: number,
    public readonly endTime: number,
  ) {
    makeObservable(this);
    this.startTime = startTime;
    this.endTime = endTime;
  }
}

export type ClipModifications = {
  trimmed?: {
    originalTimestamps: BeginEndTimestamp;
    newTimestamps: BeginEndTimestamp;
  };
};

export class Clip {
  readonly beginEndTimestamp: BeginEndTimestamp;
  modifications: ClipModifications = {};

  @computed
  get duration() {
    return this.beginEndTimestamp.endTime - this.beginEndTimestamp.startTime;
  }

  @computed
  get mediaId() {
    return this.extendedMediaId.mediaId;
  }

  @computed
  get libraryId() {
    return this.extendedMediaId.libraryId;
  }

  @computed
  get startTime() {
    return this.beginEndTimestamp.startTime;
  }

  @computed
  get endTime() {
    return this.beginEndTimestamp.endTime;
  }

  constructor(
    public readonly id: string,
    public readonly extendedMediaId: ExtendedMediaId,
    beginEndTimestamp: BeginEndTimestamp,
    modifications: Record<string, any> = {},
  ) {
    makeObservable(this);
    this.id = id;
    this.extendedMediaId = extendedMediaId;
    this.beginEndTimestamp = beginEndTimestamp;
    this.modifications = modifications;
  }
}

export class MomentMergeStore extends ReactionManager {
  @observable
  clips: Clip[] = [];

  @computed
  get canMerge() {
    const clips = this.clips;

    // Check if there are any clips
    if (clips.length === 0) {
      return false;
    }

    // Check if any clip has a duration of 1 millisecond or less
    for (const clip of clips) {
      if (clip.duration <= 0.001) {
        return false;
      }
    }

    return true;
  }

  @observable
  thumbnails: Map<string, string> = new Map();

  @observable
  totalClipsDuration: number = 0;

  // Video state properties
  @observable
  private _currentTime: number = 0;

  @observable
  private _duration: number = 0;

  @observable
  private _isPlaying: boolean = false;

  @observable
  private _isFullscreen: boolean = false;

  @observable
  private _isMuted: boolean = false;

  @observable
  private _volume: number = 100;

  @observable
  private _prevVolume: number = 100;

  @observable
  private _preview: string | null = null;

  private cumulativeDurations: number[] = [];

  private readonly renderer: Renderer;

  // Video state getters
  @computed
  get currentTime() {
    return this._currentTime;
  }

  @computed
  get duration() {
    return this._duration;
  }

  @computed
  get isPlaying() {
    return this._isPlaying;
  }

  @computed
  get isFullscreen() {
    return this._isFullscreen;
  }

  @computed
  get isMuted() {
    return this._isMuted;
  }

  @computed
  get volume() {
    return this._volume;
  }

  @computed
  get prevVolume() {
    return this._prevVolume;
  }

  @computed
  get preview() {
    return this._preview;
  }

  // Video state setters
  @action
  setCurrentTime(time: number) {
    this._currentTime = time;
  }

  @action
  setDuration(duration: number) {
    this._duration = duration;
  }

  @action
  setIsPlaying(isPlaying: boolean) {
    this._isPlaying = isPlaying;
  }

  @action
  setIsFullscreen(isFullscreen: boolean) {
    this._isFullscreen = isFullscreen;
  }

  @action
  setIsMuted(isMuted: boolean) {
    this._isMuted = isMuted;
  }

  @action
  setVolume(volume: number) {
    this._volume = volume;
  }

  @action
  setPrevVolume(prevVolume: number) {
    this._prevVolume = prevVolume;
  }

  @action
  setPreview(preview: string | null) {
    this._preview = preview;
  }

  private updateTotalDuration() {
    this.totalClipsDuration = this.clips.reduce((total, clip) => total + clip.duration, 0);

    // Update cumulative durations for binary search
    this.cumulativeDurations = [];
    let cumulative = 0;
    for (const clip of this.clips) {
      cumulative += clip.duration;
      this.cumulativeDurations.push(cumulative);
    }
  }

  clipInTimestamp(globalTimestamp: number): {
    currentClipIndex: number;
    timeInClip: number;
    timeRemainingInClip: number;
  } | null {
    if (
      this.clips.length === 0 ||
      globalTimestamp < 0 ||
      globalTimestamp > this.totalClipsDuration
    ) {
      return null;
    }

    // Binary search to find the clip index
    let left = 0;
    let right = this.cumulativeDurations.length - 1;
    let clipIndex = 0;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const cumulativeTime = this.cumulativeDurations[mid];

      if (globalTimestamp <= cumulativeTime) {
        clipIndex = mid;
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    const clip = this.clips[clipIndex];
    const previousCumulative = clipIndex > 0 ? this.cumulativeDurations[clipIndex - 1] : 0;
    const timeInClip = globalTimestamp - previousCumulative;
    const timeRemainingInClip = clip.duration - timeInClip;

    return {
      currentClipIndex: clipIndex,
      timeInClip,
      timeRemainingInClip,
    };
  }

  @action
  addClip(clip: Clip) {
    this.clips.push(clip);
    this.updateTotalDuration();
  }

  @action
  addClips(clips: Clip[]) {
    this.clips.push(...clips);
    this.updateTotalDuration();
  }

  @action
  setClips(clips: Clip[]) {
    this.clips = clips;
    this.updateTotalDuration();
  }

  @action
  setThumbnail(clipId: string, thumbnail: string) {
    this.thumbnails.set(clipId, thumbnail);
  }

  @action
  getThumbnail(clipId: string): string | undefined {
    return this.thumbnails.get(clipId);
  }

  @action
  reorderClips(fromIndex: number, toIndex: number) {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= this.clips.length ||
      toIndex > this.clips.length
    ) {
      return;
    }

    const newClips = [...this.clips];
    const [movedClip] = newClips.splice(fromIndex, 1);
    newClips.splice(toIndex, 0, movedClip);
    this.clips = newClips;
    this.updateTotalDuration();
  }

  @action
  removeClip(index: number) {
    if (index < 0 || index >= this.clips.length) {
      return;
    }

    // Get the clip being removed
    const removedClip = this.clips[index];
    const removedClipDuration = removedClip.duration;

    // Calculate the cumulative duration up to the removed clip
    const cumulativeDurationBeforeRemoved = this.clips
      .slice(0, index)
      .reduce((total, clip) => total + clip.duration, 0);

    // Determine if the current time is within the removed clip
    const isCurrentTimeInRemovedClip =
      this._currentTime >= cumulativeDurationBeforeRemoved &&
      this._currentTime < cumulativeDurationBeforeRemoved + removedClipDuration;

    // Determine if the current time is after the removed clip
    const isCurrentTimeAfterRemovedClip =
      this._currentTime >= cumulativeDurationBeforeRemoved + removedClipDuration;

    // Adjust the current time based on the deletion
    if (isCurrentTimeInRemovedClip) {
      // Current time was within the removed clip - seek to the start of the clip that will be at the same index position after deletion
      if (index < this.clips.length - 1) {
        // There's a clip after this one, seek to the start of that clip (which will become the new clip at this index)
        const nextClip = this.clips[index + 1];
        const nextClipDuration = nextClip.duration;

        // Seek to 0.01s into the clip if it's longer than 0.01s, otherwise seek to the start
        const seekOffset = nextClipDuration > 0.01 ? 0.01 : 0;
        this._currentTime = cumulativeDurationBeforeRemoved + seekOffset;
      } else if (index > 0) {
        // This was the last clip, seek to the start of the previous clip
        const previousClip = this.clips[index - 1];
        const previousClipDuration = previousClip.duration;
        const cumulativeDurationBeforePrevious = this.clips
          .slice(0, index - 1)
          .reduce((total, clip) => total + clip.duration, 0);

        // Seek to 0.01s into the clip if it's longer than 0.01s, otherwise seek to the start
        const seekOffset = previousClipDuration > 0.01 ? 0.01 : 0;
        this._currentTime = cumulativeDurationBeforePrevious + seekOffset;
      } else {
        // This was the only clip, seek to timestamp 0
        this._currentTime = 0;
      }
    } else if (isCurrentTimeAfterRemovedClip) {
      // Current time was after the removed clip, adjust by removing the duration of the deleted clip
      this._currentTime = Math.max(0, this._currentTime - removedClipDuration);
    }
    // If current time was before the removed clip, no adjustment needed

    // Remove the clip
    const newClips = this.clips.filter((_, i) => i !== index);
    this.clips = newClips;

    // Update total duration (this will also update cumulative durations)
    this.updateTotalDuration();

    // Ensure current time doesn't exceed the new total duration
    if (this._currentTime > this.totalClipsDuration) {
      this._currentTime = Math.max(0, this.totalClipsDuration);
    }
  }

  @action
  duplicateClip(index: number) {
    if (index < 0 || index >= this.clips.length) {
      return;
    }

    const clipToDuplicate = this.clips[index];
    const duplicatedClip = new Clip(
      `${clipToDuplicate.id}_duplicate_${crypto.randomUUID()}`,
      new ExtendedMediaId(clipToDuplicate.mediaId, clipToDuplicate.libraryId),
      new BeginEndTimestamp(clipToDuplicate.startTime, clipToDuplicate.endTime),
      clipToDuplicate.modifications,
    );

    // Insert the duplicate right after the original
    const newClips = [...this.clips];
    newClips.splice(index + 1, 0, duplicatedClip);
    this.clips = newClips;
    this.updateTotalDuration();
  }

  @action
  updateClipTimestamps(clipId: string, startTime: number, endTime: number) {
    const clipIndex = this.clips.findIndex((clip) => clip.id === clipId);
    if (clipIndex === -1) {
      return;
    }

    const clip = this.clips[clipIndex];
    const oldDuration = clip.duration;
    const newTimestamps = new BeginEndTimestamp(startTime, endTime);
    const newDuration = newTimestamps.endTime - newTimestamps.startTime;

    // Calculate the cumulative duration up to this clip
    const cumulativeDurationBeforeClip = this.clips
      .slice(0, clipIndex)
      .reduce((total, clip) => total + clip.duration, 0);

    // Determine if the current time is within this clip
    const isCurrentTimeInThisClip =
      this._currentTime >= cumulativeDurationBeforeClip &&
      this._currentTime < cumulativeDurationBeforeClip + oldDuration;

    // Use existing original timestamps if they exist, otherwise use current timestamps as original
    const originalTimestamps = clip.modifications.trimmed?.originalTimestamps
      ? clip.modifications.trimmed.originalTimestamps
      : new BeginEndTimestamp(clip.startTime, clip.endTime);

    // Create modifications object to track the trim
    const modifications: ClipModifications = {
      trimmed: {
        originalTimestamps,
        newTimestamps,
      },
    };

    const updatedClip = new Clip(clip.id, clip.extendedMediaId, newTimestamps, modifications);

    const newClips = [...this.clips];
    newClips[clipIndex] = updatedClip;
    this.clips = newClips;

    // Update total duration (this will also update cumulative durations)
    this.updateTotalDuration();

    // Adjust current time if it was within the modified clip
    if (isCurrentTimeInThisClip) {
      // Calculate the relative position within the clip (0.0 to 1.0)
      const relativePosition = (this._currentTime - cumulativeDurationBeforeClip) / oldDuration;

      // Apply the same relative position to the new duration
      const newTimeInClip = relativePosition * newDuration;
      this._currentTime = cumulativeDurationBeforeClip + newTimeInClip;
    }

    // Ensure current time doesn't exceed the new total duration
    if (this._currentTime > this.totalClipsDuration) {
      this._currentTime = Math.max(0, this.totalClipsDuration);
    }
  }

  @action
  async renderClips({
    filename,
    extension,
    watermark = true,
  }: {
    filename: string;
    extension: 'mp4' | 'gif';
    watermark?: boolean;
  }) {
    const result = await this.renderer.createCompilation(
      this.clips.map((clip) => ({
        libraryId: clip.libraryId,
        mediaId: clip.mediaId,
        startTime: clip.startTime,
        endTime: clip.endTime,
      })),
      `${filename}.${extension}`,
      720,
      watermark,
    );
    return result;
  }

  constructor(renderer: Renderer) {
    super();
    makeObservable(this);
    this.renderer = renderer;
  }

  init() {
    //
  }

  override deinit() {
    super.deinit();
  }
}

export const { Provider: MomentMergeProvider, useStore: useMomentMergeStore } = createContext(
  MomentMergeStore,
  {
    dispose: (store) => store.deinit(),
  },
);
